import { Badge } from "@/components/ui/badge";

export const STATE_LABEL = { draft: "Draft", pending_approval: "Pending approval", approved: "Approved", live: "Live", retired: "Retired" };
const STATE_TONE = {
  draft: "bg-slate-100 text-slate-700 border-slate-300",
  pending_approval: "bg-amber-50 text-amber-800 border-amber-300",
  approved: "bg-blue-50 text-blue-700 border-blue-300",
  live: "bg-emerald-50 text-emerald-700 border-emerald-300",
  retired: "bg-slate-50 text-slate-500 border-slate-200",
};
export const VERDICT_TONE = {
  approve: "bg-emerald-50 text-emerald-700 border-emerald-300",
  refer: "bg-amber-50 text-amber-800 border-amber-300",
  decline: "bg-rose-50 text-rose-700 border-rose-300",
};

export function StateBadge({ state, className = "" }) {
  return (
    <Badge variant="outline" className={`text-[10px] uppercase tracking-wide whitespace-nowrap ${STATE_TONE[state] || STATE_TONE.draft} ${className}`} data-testid={`state-badge-${state}`}>
      {STATE_LABEL[state] || state}
    </Badge>
  );
}

export const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

export const fmtAgo = (iso) => {
  if (!iso) return "—";
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
  const d = Math.round(h / 24);
  return d < 30 ? `${d} day${d === 1 ? "" : "s"} ago` : fmtDate(iso);
};

export const who = (v, key) => (v ? v[`${key}_name`] || v[key] || "—" : "—");

const gbp = new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 2 });

export function fmtValue(v, unit) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number") {
    if (unit === "£") return gbp.format(v).replace(/\.00$/, "");
    if (unit === "%") return `${v}%`;
    if (unit === "x") return `${v}×`;
    return unit ? `${v} ${unit}` : String(v);
  }
  if (Array.isArray(v)) {
    if (v.length === 0) return "—";
    if (v.every((x) => x === null || typeof x !== "object")) return v.join(", ");
    return JSON.stringify(v);
  }
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

export function condText(c) {
  if (!c || typeof c !== "object") return "—";
  if (Array.isArray(c.all)) return c.all.length ? `(${c.all.map(condText).join(" AND ")})` : "(empty ALL)";
  if (Array.isArray(c.any)) return c.any.length ? `(${c.any.map(condText).join(" OR ")})` : "(empty ANY)";
  if (c.not) return `NOT ${condText(c.not)}`;
  const val = c.value === undefined ? "" : ` ${JSON.stringify(c.value)}`;
  return `${c.field || "?"} ${c.operator || "?"}${val}`;
}

export const isCondSchema = (s) => !!(s && (s.$ref || s.$id === "#condition"));

export function errMsg(e) {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Request failed";
  if (typeof d === "string") return d;
  const errs = (d.errors || []).map((x) => (x.path ? `${x.path}: ${x.error || x.message || JSON.stringify(x)}` : x.error || JSON.stringify(x)));
  return [d.message, ...errs.slice(0, 6)].filter(Boolean).join(" · ");
}

export function errList(e) {
  const d = e?.response?.data?.detail;
  return d && typeof d === "object" && Array.isArray(d.errors) ? d.errors : [];
}

export function JsonBlock({ value, className = "", testId }) {
  return (
    <pre className={`text-[11px] leading-relaxed font-mono bg-slate-900 text-slate-100 rounded-md p-3 overflow-auto max-h-[420px] ${className}`} data-testid={testId}>
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

export function ValueCell({ value, unit, schema }) {
  if (isCondSchema(schema)) return <code className="text-[11px] text-indigo-700 break-words">{condText(value)}</code>;
  if (value && typeof value === "object") {
    return <code className="text-[11px] text-slate-700 break-all" title={JSON.stringify(value, null, 2)}>{fmtValue(value, unit)}</code>;
  }
  const empty = value === null || value === undefined || value === "";
  return <span className={`font-mono text-sm ${empty ? "text-slate-300" : "text-slate-900"}`}>{fmtValue(value, unit)}</span>;
}

const KIND_TONE = { added: "bg-emerald-50 text-emerald-700 border-emerald-300", removed: "bg-rose-50 text-rose-700 border-rose-300", changed: "bg-blue-50 text-blue-700 border-blue-300" };

// Reasons are keyed by the change path (e.g. "lender_table.rows[+]"); diff rows address the resulting leaves.
export function reasonFor(path, reasons = {}) {
  if (reasons[path]) return reasons[path];
  let p = path;
  while (p) {
    const m = p.match(/^(.*)(\[[^\]]*\]|\.[^.[\]]+)$/);
    if (!m) break;
    const parent = m[1];
    if (reasons[parent]) return reasons[parent];
    if (m[2].startsWith("[") && reasons[`${parent}[+]`]) return reasons[`${parent}[+]`];
    p = parent;
  }
  return "";
}

export function DiffTable({ changes = [], reasons = {}, testId = "policy-diff-table" }) {
  if (!changes.length) return <Empty testId={`${testId}-empty`}>No differences — this document matches Live setting for setting.</Empty>;
  return (
    <div className="overflow-auto rounded-md border border-slate-200" data-testid={testId}>
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide text-[10px]">
          <tr><th className="text-left p-2">Setting</th><th className="text-left p-2">Live</th><th className="text-left p-2">This version</th><th className="text-left p-2">Reason</th></tr>
        </thead>
        <tbody>
          {changes.map((c) => (
            <tr key={c.path} className="border-t border-slate-100 align-top" data-testid="policy-diff-row">
              <td className="p-2"><Badge variant="outline" className={`text-[9px] mr-1 ${KIND_TONE[c.kind] || ""}`}>{c.kind}</Badge><code className="text-[11px] break-all">{c.path}</code></td>
              <td className="p-2 font-mono text-slate-500 break-all max-w-[220px]">{fmtValue(c.old)}</td>
              <td className="p-2 font-mono text-slate-900 break-all max-w-[220px]">{fmtValue(c.new)}</td>
              <td className="p-2 text-slate-600 max-w-[260px]">{reasonFor(c.path, reasons) || <span className="text-slate-300">—</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Empty({ children, testId }) {
  return <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-md p-6 text-center" data-testid={testId}>{children}</div>;
}

export function Stat({ label, value, tone = "", testId }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 min-w-[120px]" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-sm font-semibold mt-0.5 ${tone}`}>{value}</div>
    </div>
  );
}

export const SEL = "h-8 rounded-md border border-slate-300 text-xs px-2 bg-white";
